/* ══════════════════════════════════════════════════════════════
   IST Timezone Helpers — Market Hours Detection
   All times are in Indian Standard Time (UTC+05:30)
   ══════════════════════════════════════════════════════════════ */

import { INDIA_CONFIG } from '../config/india';

const IST_OFFSET = 5.5 * 60; // +05:30 in minutes

/**
 * Get current IST Date
 */
export function getISTNow(): Date {
    const now = new Date();
    const utc = now.getTime() + now.getTimezoneOffset() * 60000;
    return new Date(utc + IST_OFFSET * 60000);
}

/**
 * Format a date in IST
 */
export function formatIST(date: Date, format: 'time' | 'date' | 'datetime' | 'full' = 'datetime'): string {
    const options: Intl.DateTimeFormatOptions = { timeZone: 'Asia/Kolkata' };

    switch (format) {
        case 'time':
            return date.toLocaleTimeString('en-IN', { ...options, hour: '2-digit', minute: '2-digit', hour12: true });
        case 'date':
            return date.toLocaleDateString('en-IN', { ...options, day: 'numeric', month: 'short', year: 'numeric' });
        case 'datetime':
            return date.toLocaleString('en-IN', { ...options, day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true });
        case 'full':
            return date.toLocaleString('en-IN', { ...options, weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
    }
}

/**
 * Get current market status
 */
export function getMarketStatus(): { status: 'pre-open' | 'open' | 'closed' | 'post-close'; label: string; emoji: string } {
    const now = getISTNow();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const time = hours * 60 + minutes;

    // Check weekend
    const day = now.getDay();
    if (day === 0 || day === 6) {
        return { status: 'closed', label: 'Market Closed (Weekend)', emoji: '🔴' };
    }

    // Check holiday
    const dateStr = now.toISOString().split('T')[0];
    if ((INDIA_CONFIG.holidays2026 as readonly string[]).includes(dateStr)) {
        return { status: 'closed', label: 'Market Holiday', emoji: '🔴' };
    }

    // Pre-open: 9:00 - 9:15
    if (time >= 540 && time < 555) {
        return { status: 'pre-open', label: 'Pre-Open Session', emoji: '🟡' };
    }

    // Market open: 9:15 - 15:30
    if (time >= 555 && time < 930) {
        return { status: 'open', label: 'Market Open', emoji: '🟢' };
    }

    // Post-close: 15:40 - 16:00
    if (time >= 940 && time < 960) {
        return { status: 'post-close', label: 'Closing Session', emoji: '🟠' };
    }

    return { status: 'closed', label: 'Market Closed', emoji: '🔴' };
}

/**
 * Check if MCX is currently trading
 * MCX hours: 9:00 AM – 11:30 PM IST
 */
export function isMCXOpen(): boolean {
    const now = getISTNow();
    const day = now.getDay();
    if (day === 0 || day === 6) return false;

    const hours = now.getHours();
    const minutes = now.getMinutes();
    const time = hours * 60 + minutes;

    return time >= 540 && time < 1410; // 9:00 to 23:30
}

/**
 * Get time until market open/close
 */
export function getTimeUntilEvent(): { event: string; minutes: number; label: string } | null {
    const now = getISTNow();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const currentTime = hours * 60 + minutes;

    const day = now.getDay();
    if (day === 0 || day === 6) return null;

    // Before market open
    if (currentTime < 555) {
        const minsUntil = 555 - currentTime;
        return { event: 'open', minutes: minsUntil, label: `Opens in ${formatDuration(minsUntil)}` };
    }

    // During market hours
    if (currentTime >= 555 && currentTime < 930) {
        const minsUntil = 930 - currentTime;
        return { event: 'close', minutes: minsUntil, label: `Closes in ${formatDuration(minsUntil)}` };
    }

    return null;
}

function formatDuration(minutes: number): string {
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hrs > 0) return `${hrs}h ${mins}m`;
    return `${mins}m`;
}

/**
 * Check if given date is a trading day
 */
export function isTradingDay(date: Date = new Date()): boolean {
    const day = date.getDay();
    if (day === 0 || day === 6) return false;

    const dateStr = date.toISOString().split('T')[0];
    return !(INDIA_CONFIG.holidays2026 as readonly string[]).includes(dateStr);
}

/**
 * Get relative time string
 */
export function getRelativeTime(date: Date | string): string {
    const d = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);

    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHrs = Math.floor(diffMin / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    const diffDays = Math.floor(diffHrs / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatIST(d, 'date');
}
